import { useQuery } from "@tanstack/react-query";
import { PhoneIncoming, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { CLIENT_STAGES, labelOf } from "../gulaConstants";
import { fullName, type GulaClient } from "../gulaTypes";
import { useAgents } from "../useAgents";
import { recentlyCalled, searchClients, type Row } from "./incomingSearch";
import { agoLabel } from "./pendingCall";

/**
 * "Me llaman": WhatsApp Business is ringing and the CRM can't see it, so the
 * setter finds the caller in two taps (the people they just called first).
 */
export const IncomingCallSheet = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [text, setText] = useState("");
  const navigate = useNavigate();
  const { meId } = useAgents();
  const { data: rows = [], isPending } = useQuery({
    queryKey: ["gula-incoming-call-clients"],
    enabled: open,
    staleTime: 30_000,
    meta: { persist: false },
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_clients_summary")
        .select(
          "id, first_name, last_name, phone, stage, sales_id, zone, last_call_attempt_at",
        )
        .order("created_at", { ascending: false })
        .limit(5000);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const list = useMemo(() => {
    if (text.trim()) return searchClients(rows, text).slice(0, 30);
    const recent = recentlyCalled(rows);
    // Mine first: they are the ones most likely calling back
    return [
      ...recent.filter((row) => String(row.sales_id) === String(meId)),
      ...recent.filter((row) => String(row.sales_id) !== String(meId)),
    ].slice(0, 30);
  }, [rows, text, meId]);

  const pick = (row: Row) => {
    setText("");
    onClose();
    navigate(`/?cliente=${row.id}`);
  };

  return (
    <Sheet open={open} onOpenChange={(value) => !value && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[88dvh] gap-0 rounded-t-3xl pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <SheetHeader className="pb-2">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <PhoneIncoming className="h-5 w-5" />
            ¿Quién te llama?
          </SheetTitle>
          <SheetDescription>
            Contesta en WhatsApp y búscalo aquí para tener su ficha delante.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Últimas cifras del número o nombre"
              inputMode="search"
              className="h-12 rounded-2xl pl-9 text-base"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4">
          {!text.trim() ? (
            <p className="pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Llamados en las últimas 48 h
            </p>
          ) : null}
          {isPending ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Cargando…
            </p>
          ) : list.length ? (
            <ul className="flex flex-col gap-1.5 pb-4">
              {list.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => pick(row)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-2xl border px-3 py-3 text-left active:bg-muted",
                      String(row.sales_id) === String(meId) &&
                        "border-lime-400 bg-lime-50 dark:bg-lime-300/10",
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold">
                        {fullName(row as GulaClient) || "Sin nombre"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[
                          row.phone,
                          labelOf(CLIENT_STAGES, row.stage),
                          row.zone,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    {row.last_call_attempt_at ? (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {agoLabel(row.last_call_attempt_at)}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {text.trim()
                ? "No hay nadie con ese número o nombre. Si es nuevo, entrará solo como lead cuando escriba por WhatsApp."
                : "No has llamado a nadie en las últimas 48 h. Busca por número o nombre."}
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
